import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://azamelectricals.tech/api';

export const fileUrl = (filePath) => {
  if (!filePath) return '';
  const normalized = String(filePath).replace(/\\/g, '/');
  if (/^https?:\/\//i.test(normalized)) return normalized;
  const uploadsIndex = normalized.toLowerCase().lastIndexOf('/uploads/');
  if (uploadsIndex >= 0) return `${API_BASE_URL}${normalized.slice(uploadsIndex)}`;
  return `${API_BASE_URL}/${normalized.replace(/^\/+/, '')}`;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// Real-Time Persistent SWR Cache Engine
// ============================================================================
// 1. Dual-tier: L1 RAM (Map) + L2 Tab-Persistent (sessionStorage)
// 2. On browser refresh / F5, cache is rehydrated synchronously in 0ms.
// 3. Refresh and mutations do NOT wipe the cache; they update the cache and
//    revalidate in the background, serving instant 0ms data continuously.
// 4. In-flight deduplication prevents redundant network calls.
// 5. Automatic storage quota protection with safe pruning.
// ============================================================================

const CACHE_STORAGE_KEY = 'erp_api_swr_cache_v2';
const GET_CACHE_FRESH_TTL_MS = 60 * 1000;          // 60 seconds completely fresh (0ms from RAM)
const GET_CACHE_STALE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours usable stale for instant 0ms render
const getResponseCache = new Map();
const inFlightGetRequests = new Map();
const originalGet = api.get.bind(api);

const getStoredUserId = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')?.id || '';
  } catch {
    return '';
  }
};

const normalizeParams = (params) => {
  if (!params || typeof params !== 'object') return {};
  const sorted = {};
  Object.keys(params)
    .sort()
    .forEach((k) => {
      if (params[k] !== undefined && params[k] !== null && params[k] !== '') {
        sorted[k] = params[k];
      }
    });
  return sorted;
};

const requestCacheKey = (url, config = {}) =>
  JSON.stringify({
    url: String(url || '').trim().toLowerCase(),
    params: normalizeParams(config.params),
    companyId: localStorage.getItem('companyId') || '',
    userId: getStoredUserId(),
  });

const createCachedAxiosResponse = (cachedItem) => ({
  data: cachedItem.data,
  status: cachedItem.status || 200,
  statusText: cachedItem.statusText || 'OK',
  headers: cachedItem.headers || {},
  config: { url: cachedItem.url, params: cachedItem.params },
  __isCached: true,
  __cachedAt: cachedItem.timestamp,
});

// Synchronously rehydrate cache from sessionStorage on startup
const rehydrateCacheFromStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    const raw = sessionStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return;
    const now = Date.now();
    for (const [key, item] of Object.entries(parsed)) {
      if (!item || item.data === undefined) continue;
      // Stale or fresh within 24 hours is preserved
      if (!item.staleUntil || now < item.staleUntil) {
        getResponseCache.set(key, {
          freshUntil: item.freshUntil || 0,
          staleUntil: item.staleUntil || now + GET_CACHE_STALE_TTL_MS,
          timestamp: item.timestamp || now,
          response: createCachedAxiosResponse(item),
        });
      }
    }
  } catch (err) {
    console.warn('Cache rehydration error:', err);
  }
};

// Immediately rehydrate on module load
rehydrateCacheFromStorage();

// Throttled persistence to sessionStorage (avoids excessive writes)
let persistTimer = null;
const persistCacheToStorage = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    try {
      const serializable = {};
      let count = 0;
      for (const [key, val] of getResponseCache.entries()) {
        if (!val?.response?.data || count >= 120) break;
        // Don't store if data is gigantic (>500KB) to respect quota
        const data = val.response.data;
        serializable[key] = {
          data,
          status: val.response.status,
          statusText: val.response.statusText,
          timestamp: val.timestamp || Date.now(),
          freshUntil: val.freshUntil,
          staleUntil: val.staleUntil,
        };
        count++;
      }
      sessionStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(serializable));
    } catch (err) {
      try {
        sessionStorage.removeItem(CACHE_STORAGE_KEY);
      } catch {}
    }
  }, 200);
};

// Synchronously read cached data if present (0ms)
export const getApiCacheSync = (url, config = {}) => {
  const key = requestCacheKey(url, config);
  const entry = getResponseCache.get(key);
  if (entry && entry.response) {
    return entry.response;
  }
  return null;
};

// Update a cache entry without clearing existing data
export const updateApiCacheEntry = (url, config, response) => {
  if (!response || response.config?.responseType === 'blob') return;
  const key = requestCacheKey(url, config);
  const now = Date.now();
  getResponseCache.set(key, {
    freshUntil: now + GET_CACHE_FRESH_TTL_MS,
    staleUntil: now + GET_CACHE_STALE_TTL_MS,
    timestamp: now,
    response,
  });
  persistCacheToStorage();
};

// Mark cache entries as stale so they update in background rather than getting cleared
export const markCacheStale = (options = {}) => {
  const targetPrefix = options.prefix ? String(options.prefix).toLowerCase() : null;
  for (const [key, val] of getResponseCache.entries()) {
    if (!targetPrefix || key.toLowerCase().includes(targetPrefix)) {
      val.freshUntil = 0;
    }
  }
  persistCacheToStorage();
};

// Never clear cache unless hardClear is explicitly requested (e.g. on logout)
export const clearApiGetCache = (hardClear = false) => {
  if (hardClear) {
    getResponseCache.clear();
    inFlightGetRequests.clear();
    try {
      sessionStorage.removeItem(CACHE_STORAGE_KEY);
    } catch {}
  } else {
    // Soft update: mark stale so data remains instantly visible in 0ms while revalidating
    markCacheStale();
  }
};

api.get = (url, config = {}) => {
  // Files, blobs must always be retrieved live
  if (config.responseType === 'blob') {
    return originalGet(url, config);
  }

  // Explicit live bypass (e.g. force refresh from server)
  if (config.skipCache || config.forceLive) {
    const liveRequest = originalGet(url, config);
    liveRequest
      .then((response) => {
        updateApiCacheEntry(url, config, response);
      })
      .catch(() => {});
    return liveRequest;
  }

  const key = requestCacheKey(url, config);
  const cached = getResponseCache.get(key);
  const now = Date.now();

  // 1. Fresh Cache hit: Instant 0ms response (<1ms microtask)
  if (cached && now < cached.freshUntil) {
    return Promise.resolve(cached.response);
  }

  // 2. Stale Cache hit: Instant 0ms response + silent background revalidation (SWR)
  if (cached && now < cached.staleUntil) {
    if (!inFlightGetRequests.has(key)) {
      const bgPromise = originalGet(url, config)
        .then((freshResponse) => {
          updateApiCacheEntry(url, config, freshResponse);
          window.dispatchEvent(
            new CustomEvent('erp:swr-updated', { detail: { url, key, data: freshResponse.data } })
          );
          return freshResponse;
        })
        .catch((err) => {
          console.warn('Background SWR revalidation error for:', url, err?.message);
        })
        .finally(() => {
          inFlightGetRequests.delete(key);
        });
      inFlightGetRequests.set(key, bgPromise);
    }
    return Promise.resolve(cached.response);
  }

  // 3. In-flight request deduplication: reuse active request to eliminate duplicate roundtrips
  const inFlight = inFlightGetRequests.get(key);
  if (inFlight) {
    return inFlight;
  }

  // 4. Cold fetch: fetch live, cache, persist, and resolve
  const request = originalGet(url, config)
    .then((response) => {
      updateApiCacheEntry(url, config, response);
      return response;
    })
    .finally(() => {
      inFlightGetRequests.delete(key);
    });

  inFlightGetRequests.set(key, request);
  return request;
};

const isJwtExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    return payload.exp * 1000 <= Date.now();
  } catch (error) {
    return true;
  }
};

const clearStoredAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('companyId');
  clearApiGetCache(true); // Hard clear only on explicit logout!
};

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle responses
api.interceptors.response.use(
  (response) => {
    if (response.config?.method?.toLowerCase() !== 'get') {
      // Soft mark stale: do NOT wipe cache! Update in background while keeping 0ms response
      markCacheStale();
      window.dispatchEvent(
        new CustomEvent('erp:data-changed', {
          detail: { url: response.config?.url, method: response.config?.method },
        })
      );
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem('token');
      if (!token || isJwtExpired(token)) {
        clearStoredAuth();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  registerCompanyAdmin: (data) => api.post('/auth/register-company-admin', data),
  verifyOtp: (data) => api.post('/auth/verify-otp', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  profile: () => api.get('/auth/profile'),
};

// Dashboard APIs
export const dashboardAPI = {
  getDashboard: () => api.get('/dashboard'),
  getDashboardStats: () => api.get('/dashboard/stats'),
  getAdminDashboard: (companyId) => api.get(`/dashboard/admin/${companyId}`),
  getEngineerDashboard: (companyId) => api.get(`/dashboard/engineer/${companyId}`),
  getProcurementDashboard: (companyId) => api.get(`/dashboard/procurement/${companyId}`),
  getStoreDashboard: (companyId) => api.get(`/dashboard/store/${companyId}`),
  getAccountDashboard: (companyId) => api.get(`/dashboard/account/${companyId}`),
};

// User APIs
export const userAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Company APIs
export const companyAPI = {
  getAll: () => api.get('/companies'),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.patch(`/companies/${id}`, data),
  getProfile: (id) => api.get(`/companies/${id}/profile`),
  getStats: (id) => api.get(`/companies/${id}/stats`),
  getVerification: (id) => api.get(`/companies/${id}/verify`),
  getPending: () => api.get('/companies/admin/pending'),
  getSuperAdminDashboard: (params) => api.get('/companies/admin/dashboard', { params }),
  approve: (data) => api.post('/companies/admin/approve', data),
  getCompanyDetails: (companyId) => api.get(`/companies/admin/${companyId}/details`),
  updateStatus: (companyId, data) => api.patch(`/companies/admin/${companyId}/status`, data),
  getAllUsersForSuperAdmin: (params) => api.get('/companies/admin/all-users', { params }),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.patch(`/companies/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Material APIs
export const materialAPI = {
  getAll: (params) => api.get('/material', { params }),
  getById: (id) => api.get(`/material/${id}`),
  create: (data) => api.post('/material', data),
  update: (id, data) => api.patch(`/material/${id}`, data),
  delete: (id) => api.delete(`/material/${id}`),
  bulkImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/materials/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// BOQ APIs
export const boqAPI = {
  getAll: (params) => api.get('/boq', { params }),
  getById: (id) => api.get(`/boq/${id}`),
  create: (data) => api.post('/boq', data),
  update: (id, data) => api.patch(`/boq/${id}`, data),
  delete: (id) => api.delete(`/boq/${id}`),
  submit: (id) => api.post(`/boq/${id}/submit`, {}),
approve: (id, data) => {
  console.log("APPROVE ID:", id); // 🔥 DEBUG
  return api.post(`/boq/${id}/approve`, data);
},

reject: (id, data) => {
  console.log("REJECT ID:", id); // 🔥 DEBUG
  return api.post(`/boq/${id}/reject`, data);
},
};

// Indent APIs
export const indentAPI = {
  getAll: (params) => api.get('/indent', { params }),
  getById: (id) => api.get(`/indent/${id}`),
  create: (data) => api.post('/indent', data),
  update: (id, data) => api.patch(`/indent/${id}`, data),
  delete: (id) => api.delete(`/indent/${id}`),
  approve: (id, data) => api.post(`/indent/${id}/approve`, data),
  reject: (id, data) => api.post(`/indent/${id}/reject`, data),
};

// Purchase Order APIs
export const poAPI = {
  getAll: (params) => api.get('/po', { params }),
  getById: (id) => api.get(`/po/${id}`),
  create: (data) => api.post('/po', data),
  getQuotations: (params) => api.get('/po/quotations', { params }),
  createQuotation: (data) => api.post('/po/quotations', data),
  uploadQuotation: (data) => {
    const formData = new FormData();
    (data.files || []).forEach((file) => formData.append('files', file));
    (data.buyInquiryIds || []).forEach((id) => formData.append('buyInquiryIds', id));
    if (data.supplierId) formData.append('supplierId', data.supplierId);
    if (data.supplierName) formData.append('supplierName', data.supplierName);
    if (data.quotationNumber) formData.append('quotationNumber', data.quotationNumber);
    if (data.validUntil) formData.append('validUntil', data.validUntil);
    if (data.notes) formData.append('notes', data.notes);
    if (data.items) formData.append('items', JSON.stringify(data.items));
    if (data.supplierDetails) formData.append('supplierDetails', JSON.stringify(data.supplierDetails));
    if (data.reviewedQuotations) formData.append('reviewedQuotations', JSON.stringify(data.reviewedQuotations));
    return api.post('/po/quotations/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  extractQuotations: (data) => {
    const formData = new FormData();
    (data.files || []).forEach((file) => formData.append('files', file));
    (data.buyInquiryIds || []).forEach((id) => formData.append('buyInquiryIds', id));
    return api.post('/po/quotations/extract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  decideQuotation: (quotationId, data) => api.post(`/po/quotations/${quotationId}/decision`, data),
  updateQuotation: (quotationId, data) => api.post(`/po/quotations/${quotationId}/update`, data),
  releasePoFromQuotation: (quotationId, data = {}) => api.post(`/po/quotations/${quotationId}/release-po`, data),
  addQuotation: (poId, data) => api.post(`/po/${poId}/quotation`, { ...data, poId }),
  selectQuotation: (poId, quotationId) => api.post(`/po/${poId}/quotation/${quotationId}/select`, {}),
  sendToVendor: (id, data = {}) => api.post(`/po/${id}/send-vendor`, data),
  update: (id, data) => api.put(`/po/${id}`, data),
  delete: (id) => api.delete(`/po/${id}`),
  approve: (id, data) => api.post(`/po/${id}/approve`, data),
  decide: (id, data) => api.post(`/po/${id}/decision`, data),
  reject: (id, data) => api.post(`/po/${id}/reject`, data),
  generatePDF: (id) => api.get(`/po/${id}/pdf`, { responseType: 'blob' }),
};

// Alias for backward compatibility
export const purchaseOrderAPI = poAPI;

// Delivery Challan APIs
export const dcAPI = {
  getAll: (params) => api.get('/delivery-challan', { params }),
  getById: (id) => api.get(`/delivery-challan/${id}`),
  create: (data) => api.post('/delivery-challan', data),
  update: (id, data) => api.patch(`/delivery-challan/${id}`, data),
  delete: (id) => api.delete(`/delivery-challan/${id}`),
  approve: (id, data) => api.post(`/delivery-challan/${id}/approve`, data),
  generatePDF: (id) => api.get(`/delivery-challan/${id}/pdf`, { responseType: 'blob' }),
};

// GRN APIs
export const grnAPI = {
  getAll: (params) => api.get('/grn', { params }),
  getById: (id) => api.get(`/grn/${id}`),
  create: (data) => api.post('/grn', data),
  update: (id, data) => api.patch(`/grn/${id}`, data),
  delete: (id) => api.delete(`/grn/${id}`),
  verify: (id, data = {}) => api.post(`/grn/${id}/verify`, { ...data, grnId: id }),
  getInvoiceAlignment: (poId) => api.get(`/grn/po/${poId}/invoice-alignment`),
};

// Project APIs
export const projectAPI = {
  getAll: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.patch(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  assignEngineer: (id, data) => api.post(`/projects/${id}/engineers`, data),
  removeEngineer: (id, data) => api.delete(`/projects/${id}/engineers`, { data }),
  getAssignmentHistory: (id) => api.get(`/projects/${id}/assignment-history`),
  addWorker: (id, data) => api.post(`/projects/${id}/workers`, data),
  updateWorker: (id, workerId, data) => api.patch(`/projects/${id}/workers/${workerId}`, data),
  removeWorker: (id, workerId) => api.delete(`/projects/${id}/workers/${workerId}`),
};

// Supplier APIs
export const supplierAPI = {
  getAll: (params) => api.get('/suppliers', { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.patch(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Client APIs
export const clientAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.patch(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Extraction APIs
export const extractionAPI = {
  getAll: (params) => api.get('/extraction', { params }),
  getById: (id) => api.get(`/extraction/${id}`),
  uploadBoq: (file, projectId, boqNumber) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    formData.append('boqNumber', boqNumber);
    return api.post('/extraction/boq/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadInvoice: ({ file, poId, projectId, boqId, indentId, uploadSource }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('poId', poId);
    if (projectId) formData.append('projectId', projectId);
    if (boqId) formData.append('boqId', boqId);
    if (indentId) formData.append('indentId', indentId);
    if (uploadSource) formData.append('uploadSource', uploadSource);
    return api.post('/extraction/invoice/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadDeliveryChallan: (file, poId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('poId', poId);
    return api.post('/extraction/dc/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadStoreSupply: (file, projectId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    return api.post('/extraction/store-supply/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  approve: (id, data = {}) => api.patch(`/extraction/${id}/approve`, data),
  reject: (id, data) => api.patch(`/extraction/${id}/reject`, data),
  correctError: (errorId, manualCorrection) =>
    api.patch(`/extraction/error/${errorId}/correct`, { manualCorrection }),
};

// Inventory APIs
export const inventoryAPI = {
  getAll: (params) => api.get('/inventory/project', { params }),
  getMasterMaterialSheet: (params) => api.get('/inventory/master-material-sheet', { params }),
  getShortages: (params) => api.get('/inventory/shortages', { params }),
  getStoreSupplies: (params) => api.get('/inventory/store-supplies', { params }),
  createStoreSupply: (data) => api.post('/inventory/store-supplies', data),
  getDeliveredProjectMaterials: (projectId) =>
    api.get(`/inventory/project/${projectId}/delivered-materials`),
  getReturnReceipts: (params) => api.get('/inventory/return-receipts', { params }),
  createReturnReceipt: (data) => api.post('/inventory/return-receipts', data),
  getById: (id) => api.get(`/inventory/${id}`),
  getByMaterial: (materialId, params) =>
    api.get(`/inventory/${materialId}/company`, { params }),
  updateStock: (materialId, projectId, data) => 
    api.patch(`/inventory/${materialId}/project/${projectId}`, data),
  reserve: (materialId, projectId, data) =>
    api.post(`/inventory/${materialId}/project/${projectId}/reserve`, data),
  consume: (materialId, projectId, data) =>
    api.post(`/inventory/${materialId}/project/${projectId}/consume`, data),
  getLowStock: (projectId) => api.get(`/inventory/project/${projectId}/low-stock`),
  getOutOfStock: (projectId) => api.get(`/inventory/project/${projectId}/out-of-stock`),
};

// Delivery Challan APIs (correct endpoint)
export const deliveryChallanAPI = {
  getAll: (params) => api.get('/delivery-challan', { params }),
  getById: (id) => api.get(`/delivery-challan/${id}`),
  create: (data) => api.post('/delivery-challan', data),
  update: (id, data) => api.patch(`/delivery-challan/${id}`, data),
  delete: (id) => api.delete(`/delivery-challan/${id}`),
  getByPO: (poId) => api.get(`/delivery-challan/po/${poId}`),
  autoGRN: (id) => api.post(`/delivery-challan/${id}/auto-grn`, {}),
};

// Invoice APIs
export const invoiceAPI = {
  getAll: (params) => api.get('/invoice', { params }),
  getById: (id) => api.get(`/invoice/${id}`),
  create: (data) => api.post('/invoice', data),
  uploadPhotos: (data, files) => {
    const formData = new FormData();
    formData.append('projectId', data.projectId);
    formData.append('indentId', data.indentId);
    if (data.notes) formData.append('notes', data.notes);
    (files || []).forEach((file) => formData.append('files', file));
    return api.post('/invoice/upload-photos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  acknowledge: (id, data = {}) => api.post(`/invoice/${id}/acknowledge`, data),
  update: (id, data) => api.patch(`/invoice/${id}`, data),
  delete: (id) => api.delete(`/invoice/${id}`),
  approve: (id) => api.post(`/invoice/${id}/approve`, {}),
  markPaid: (id) => api.post(`/invoice/${id}/paid`, {}),
  markHardCopyReceived: (id, data = {}) => api.post(`/invoice/${id}/hard-copy-received`, data),
  getByCompany: (companyId) => api.get(`/invoice/company/${companyId}`),
  getSummary: (companyId) => api.get(`/invoice/summary/${companyId}`),
};

// Approval Center APIs
export const approvalCenterAPI = {
  getQueue: (params) => api.get('/approval-center', { params }),
  decide: (type, id, data) => api.post(`/approval-center/${type}/${id}/decision`, data),
  finalEdit: (type, id, data) => api.patch(`/approval-center/${type}/${id}/final-edit`, data),
};

// Buy Inquiry APIs
export const buyInquiryAPI = {
  getAll: (params) => api.get('/buy-inquiry', { params }),
  getById: (id) => api.get(`/buy-inquiry/${id}`),
  create: (data) => api.post('/buy-inquiry', data),
  createFromIndent: (indentId) => api.post(`/buy-inquiry/from-indent/${indentId}`, {}),
  
  update: (id, data) => api.post(`/buy-inquiry/${id}/update`, data),   // ← Updated
  delete: (id) => api.delete(`/buy-inquiry/${id}`),                   // ← New
  
  decide: (id, data) => api.post(`/buy-inquiry/${id}/decision`, data),
};

// Document Center APIs
export const documentCenterAPI = {
  get: (type, id) => api.get(`/documents/${type}/${id}`),
  getWorkflowGraph: (params = {}) => api.get('/documents/workflow/graph', { params }),
  searchWorkflow: (q) => api.get('/documents/workflow/search', { params: { q } }),
};

// User Invitation APIs
export const invitationAPI = {
  invite: (data) => api.post('/users/invite', data),
  bulkInvite: (data) => api.post('/users/invite/bulk', data),
  getPending: () => api.get('/users/invitations/pending'),
  getHistory: () => api.get('/users/invitations/history'),
  getByToken: (token) => api.get(`/users/invitations/${token}`),
  accept: (data) => api.post('/users/invitations/accept', data),
  reject: (token, data) => api.post(`/users/invitations/${token}/reject`, data),
  resend: (id) => api.post(`/users/invitations/${id}/resend`, {}),
  cancel: (id) => api.delete(`/users/invitations/${id}`),
};

// Company Admin APIs
export const companyAdminAPI = {
  getDashboard: (params) => api.get('/companies/admin/dashboard', { params }),
  getPending: () => api.get('/companies/admin/pending'),
  approve: (data) => api.post('/companies/admin/approve', data),
  getStats: (id) => api.get(`/companies/${id}/stats`),
  getAllUsers: (params) => api.get('/companies/admin/all-users', { params }),
};

export default api;
